import { Fragment, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
  createColumnHelper,
  SortingState,
  ColumnDef,
  RowData,
} from "@tanstack/react-table";
import { ICustomerGenericPriceSetting } from "../../../models/PriceSettings";
import PagedTable from "../../shared/Tables/PagedTable";
import SpecificPriceCell from "./SpecificPriceCell";
import { formatDate } from "@fullcalendar/core";
import React from "react";
import formatDateTime from "../../../utils/formatDateTime";
import SpecificPriceActions from "./SpecificPriceActions";
import PartsLookupCell from "./PartsLookupCell";
import PricingTypeCell from "./PricingTypeCell";
import EngineCodeMatchTypeButton from "./EngineCodeMatchTypeButton";
import Tooltip from "../../shared/Tooltip";

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    updateData: (rowIndex: number, columnId: string, value: unknown) => void
  }
}

// Give our default column cell renderer editing superpowers!
// const defaultColumn: Partial<ColumnDef<ICustomerGenericPriceSetting>> = {
//   cell: ({ getValue, row: { index }, column: { id }, table }) => {
//     const initialValue = getValue()
//     // We need to keep and update the state of the cell normally
//     const [value, setValue] = React.useState(initialValue)

//     // When the input is blurred, we'll call our table meta's updateData function
//     const onBlur = () => {
//       table.options.meta?.updateData(index, id, value)
//     }

//     // If the initialValue is changed external, sync it up with our state
//     React.useEffect(() => {
//       setValue(initialValue)
//     }, [initialValue])

//     return (
//       <input
//         value={value as string}
//         onChange={e => setValue(e.target.value)}
//         onBlur={onBlur}
//       />
//     )
//   },
// }

const minBodyConditions = [
  { id: 2, name: "Any"     },
  { id: 1, name: "Average" },
  { id: 0, name: "Good"    },
]

export default function FullSpecificPriceTable() {
  const rerender = React.useReducer(() => ({}), {})[1]

  const columns = React.useMemo<ColumnDef<ICustomerGenericPriceSetting>[]>(() => [
    {
      header: 'Rule Only Applies When ALL These Conditions Match',
      footer: props => props.column.id,
      columns: [
        { accessorKey: 'ruleId', header: 'ID', cell: (info) => <span className="mx-2">{info.getValue()}</span>, footer: props => props.column.id },
        
        { accessorKey: 'fromYear'        , header: 'From Year'     , cell: (info) => (<SpecificPriceCell priceSetting={info.row.original} info={info} prop="fromYear"         type="number" width="max-w-14"            />), footer: props => props.column.id },
        { accessorKey: 'toYear'          , header: 'To Year'       , cell: (info) => (<SpecificPriceCell priceSetting={info.row.original} info={info} prop="toYear"           type="number" width="max-w-14"            />), footer: props => props.column.id },
        { accessorKey: 'make'            , header: 'Make'          , cell: (info) => (<SpecificPriceCell priceSetting={info.row.original} info={info} prop="make"             type="combo"  width="min-w-[125px]"       />), footer: props => props.column.id },
        { accessorKey: 'model'           , header: 'Model'         , cell: (info) => (<SpecificPriceCell priceSetting={info.row.original} info={info} prop="model"            type="combo"  width="min-w-[125px]"       />), footer: props => props.column.id },
        { accessorKey: 'fuelType'        , header: 'Fuel Type'     , cell: (info) => (<SpecificPriceCell priceSetting={info.row.original} info={info} prop="fuelType"         type="text"                               />), footer: props => props.column.id },
        { accessorKey: 'transmissionType', header: 'Trans- mission', cell: (info) => (<SpecificPriceCell priceSetting={info.row.original} info={info} prop="transmissionType" type="text"                               />), footer: props => props.column.id },
        { accessorKey: 'engineCode'      , header: 'Engine Code'   , cell: (info) => (<span className="flex items-center"><SpecificPriceCell priceSetting={info.row.original} info={info} prop="engineCode" type="text" /><EngineCodeMatchTypeButton priceSetting={info.row.original} /></span>), footer: props => props.column.id },
        { accessorKey: 'fromEngineCc'    , header: 'From CC'       , cell: (info) => (<SpecificPriceCell priceSetting={info.row.original} info={info} prop="fromEngineCc"     type="number" width="max-w-14"            />), footer: props => props.column.id },
        { accessorKey: 'toEngineCc'      , header: 'To CC'         , cell: (info) => (<SpecificPriceCell priceSetting={info.row.original} info={info} prop="toEngineCc"       type="number" width="max-w-14"            />), footer: props => props.column.id },
        { accessorKey: 'minBodyCondition', header: 'Min Body'      , cell: (info) => (<SpecificPriceCell priceSetting={info.row.original} info={info} prop="minBodyCondition" type="select" options={minBodyConditions} />), footer: props => props.column.id },
      ],
    },
    {
      header: 'Fixed Price (Overrides Top Up)',
      meta: { headerClassName: "bg-yellow-300" },
      footer: props => props.column.id,
      columns: [
        { accessorKey: 'goodEngineFixedPrice', header: 'Good Engine', cell: (info) => (<SpecificPriceCell priceSetting={info.row.original} info={info} prop="goodEngineFixedPrice" type="number" bgColor="bg-yellow-100" minValue={0} />), meta: { headerClassName: "bg-yellow-200" }, footer: props => props.column.id },
        { accessorKey: 'minorFaultFixedPrice', header: 'Minor Fault', cell: (info) => (<SpecificPriceCell priceSetting={info.row.original} info={info} prop="minorFaultFixedPrice" type="number" bgColor="bg-yellow-100" minValue={0} />), meta: { headerClassName: "bg-yellow-200" }, footer: props => props.column.id },
        { accessorKey: 'majorFaultFixedPrice', header: 'Major Fault', cell: (info) => (<SpecificPriceCell priceSetting={info.row.original} info={info} prop="majorFaultFixedPrice" type="number" bgColor="bg-yellow-100" minValue={0} />), meta: { headerClassName: "bg-yellow-200" }, footer: props => props.column.id },
      ],
    },
    {
      header: 'Top Up/ Deduction (+/-)',
      meta: { headerClassName: "bg-blue-300" },
      footer: props => props.column.id,
      columns: [
        { accessorKey: 'maximumPrice'                , header: 'Max Price'   , cell: (info) => (<SpecificPriceCell priceSetting={info.row.original} info={info} prop="maximumPrice"           type="number" bgColor="bg-blue-100" minValue={0} />), meta: { headerClassName: "bg-blue-200" }, footer: props => props.column.id },
        { accessorKey: 'goodEnginePriceSetting.value', header: 'Good Engine' , cell: (info) => (<SpecificPriceCell priceSetting={info.row.original} info={info} prop="goodEnginePriceSetting" type="price"  bgColor="bg-blue-100" disabled={info.row.original.goodEngineFixedPrice != null} />), meta: { headerClassName: "bg-blue-200" }, footer: props => props.column.id },
        { accessorKey: 'minorFaultPriceSetting.value', header: 'Minor Fault' , cell: (info) => (<SpecificPriceCell priceSetting={info.row.original} info={info} prop="minorFaultPriceSetting" type="price"  bgColor="bg-blue-100" disabled={info.row.original.minorFaultFixedPrice != null} />), meta: { headerClassName: "bg-blue-200" }, footer: props => props.column.id },
        { accessorKey: 'majorFaultPriceSetting.value', header: 'Major Fault' , cell: (info) => (<SpecificPriceCell priceSetting={info.row.original} info={info} prop="majorFaultPriceSetting" type="price"  bgColor="bg-blue-100" disabled={info.row.original.majorFaultFixedPrice != null} />), meta: { headerClassName: "bg-blue-200" }, footer: props => props.column.id },
      ],
    },
    {
      header: 'Other',
      footer: props => props.column.id,
      columns: [
        { accessorKey: 'partsLookup'      , header: 'Part Pricing', cell: (info) => (<SpecificPriceCell priceSetting={info.row.original} info={info} prop="partsLookup" type="switch" />), footer: props => props.column.id },
        { accessorKey: 'partsLookupPrice' , header: 'Parts £'     , cell: (info) => (<PartsLookupCell priceSetting={info.row.original} info={info} />), footer: props => props.column.id },
        { accessorKey: 'isActive'         , header: 'Is Active'   , cell: (info) => (<SpecificPriceCell priceSetting={info.row.original} info={info} prop="isActive"    type="switch" />), footer: props => props.column.id },
        { accessorKey: 'isExclusive'      , header: () => (<Tooltip content="When this rule matches, only its top up is added">Exclusive</Tooltip>), cell: (info) => (<SpecificPriceCell priceSetting={info.row.original} info={info} prop="isExclusive" type="switch" />), footer: props => props.column.id },
        { accessorKey: 'appliesToScrap'   , header: 'Applies To'  , cell: (info) => (<PricingTypeCell priceSetting={info.row.original} info={info} />)                                     , footer: props => props.column.id },
        { accessorKey: 'updatedDate'      , header: 'Updated'     , cell: (info) => <span>{formatDateTime(info.row.original.updatedDate)}</span>                                         , footer: props => props.column.id },
        { accessorKey: 'id'               , header: ''            , cell: (info) => <SpecificPriceActions info={info} />                                                                 , footer: props => props.column.id },
      ],
    }
  ], []);

  return (
    <PagedTable
      key="generic-price-settings"
      url="/pricesetting/generic/paginated"
      columns={columns}
      queryLabel="Search settings by ID, make, model or fuel type"
      showTotal={true}
      initialSort={[{ id: "createdDate", desc: true }]}
      cellPadding="px-[0px] py-[0px]"
      fontSize="text-xs mb-20"
      containerClassName="max-h-[56vh] overflow-y-scroll"
      condensedView={true}
      insertEmptyRowUrl={"/pricesetting/generic/empty"}
    />
  )
}